import React, { useState } from 'react';
import { useApi, Spinner, ErrBanner } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import { useAuth } from '../../context/AuthContext';

const EMPTY_USER = {
  name: '', email: '', password: '', phone: '',
  role_id: '', is_active: true,
};

export default function UserSection() {
  const { can } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_USER);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [reset, setReset] = useState(null);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const { data: users, loading, error, reload } = useApi(
    () => api.users.list(), []
  );
  const { data: roles } = useApi(() => api.roles.list(), [], []);

  const startEdit = (u) => {
    setForm({
      name: u.name,
      email: u.email,
      password: '',
      phone: u.phone || '',
      role_id: u.role_id,
      is_active: !!Number(u.is_active),
    });
    setEditId(u.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim())  { notify('❌ Name required'); return; }
    if (!form.email.trim()) { notify('❌ Email required'); return; }
    if (!form.role_id)      { notify('❌ Select a role'); return; }
    if (!editId && (!form.password || form.password.length < 6)) {
      notify('❌ Password must be at least 6 characters'); return;
    }
    setSaving(true);
    try {
      if (editId) {
        await api.users.update(editId, {
          name: form.name.trim(),
          phone: form.phone.trim(),
          role_id: parseInt(form.role_id),
          is_active: form.is_active ? 1 : 0,
        });
        notify('✅ User updated');
      } else {
        await api.users.create({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          phone: form.phone.trim(),
          role_id: parseInt(form.role_id),
        });
        notify('✅ User created');
      }
      setShowForm(false); setForm(EMPTY_USER); setEditId(null); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.users.remove(confirm.id);
      notify('✅ User deactivated');
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  const handleResetPassword = async () => {
    if (!reset.newPw || reset.newPw.length < 6) {
      notify('❌ Password must be at least 6 characters'); return;
    }
    try {
      await api.users.resetPassword(reset.user.id, reset.newPw);
      notify('✅ Password reset');
      setReset(null);
    } catch (e) { notify('❌ ' + e.message); }
  };

  const assignableRoles = (roles || []).filter(r => Number(r.is_system) !== 1);

  return (
    <div className="user-section">
       <div className="section-header" style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <span>Users ({ (users || []).length })</span>
        {can('users', 'create') && (
          <button className="btn btn-primary btn-sm"
                  onClick={() => { setShowForm(!showForm); setForm(EMPTY_USER); setEditId(null); }}>
            {showForm ? '✕ Cancel' : '＋ New User'}
          </button>
        )}
      </div>

      {msg && <div className="um-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}

      {showForm && (
        <div className="section-card" style={{marginTop:16}}>
          <div className="section-header">{editId ? 'Edit User' : 'Create New User'}</div>
          <div className="um-form-grid">
            <div className="form-group">
              <label>Name *</label>
              <input value={form.name}
                     onChange={e => setForm({...form, name: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input type="email" value={form.email} disabled={!!editId}
                     onChange={e => setForm({...form, email: e.target.value})} />
            </div>
            {!editId && (
              <div className="form-group">
                <label>Password *</label>
                <input type="password" value={form.password}
                       onChange={e => setForm({...form, password: e.target.value})} />
              </div>
            )}
            <div className="form-group">
              <label>Phone</label>
              <input value={form.phone}
                     onChange={e => setForm({...form, phone: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Role *</label>
              <select value={form.role_id}
                      onChange={e => setForm({...form, role_id: e.target.value})}>
                <option value="">-- Select role --</option>
                {assignableRoles.map(r => (
                  <option key={r.id} value={r.id}>{r.role_name}</option>
                ))}
              </select>
            </div>
            {editId && (
              <div className="form-group" style={{display:'flex', alignItems:'center'}}>
                <label style={{display:'flex', alignItems:'center', gap:6, margin:0}}>
                  <input type="checkbox" checked={form.is_active}
                         onChange={e => setForm({...form, is_active: e.target.checked})} />
                  Active (user can log in)
                </label>
              </div>
            )}
          </div>
          <div style={{padding:'0 12px 12px'}}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
              {saving ? '⏳ Saving...' : (editId ? 'Update User' : 'Create User')}
            </button>
          </div>
        </div>
      )}

      {loading ? <Spinner /> : (
        <div className="section-card" style={{overflowX:'auto', marginTop:16}}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{textAlign:'left'}}>Name</th>
                <th style={{textAlign:'left'}}>Email</th>
                <th style={{textAlign:'left'}}>Role</th>
                <th style={{textAlign:'left'}}>Phone</th>
                <th>Status</th>
                <th style={{textAlign:'left'}}>Last Login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(users || []).length === 0 ? (
                <tr><td colSpan="7" style={{textAlign:'center', color:'var(--text-muted)'}}>
                  No sub-users yet. Click "+ New User" to add one.
                </td></tr>
              ) : (users || []).map(u => (
                <tr key={u.id} style={{opacity: Number(u.is_active) === 0 ? 0.55 : 1}}>
                  <td style={{textAlign:'left', fontWeight:600}}>{u.name}</td>
                  <td style={{textAlign:'left'}}>{u.email}</td>
                  <td style={{textAlign:'left'}}>
                    <span className="um-role-pill">{u.role_name}</span>
                  </td>
                  <td style={{textAlign:'left'}}>{u.phone || '—'}</td>
                  <td>
                    {Number(u.is_active) === 1
                      ? <span className="um-status-active">Active</span>
                      : <span className="um-status-inactive">Inactive</span>}
                  </td>
                  <td style={{textAlign:'left', fontSize:11.5, color:'var(--text-muted)'}}>
                    {u.last_login || 'Never'}
                  </td>
                  <td style={{display:'flex', gap:4, flexWrap:'wrap'}}>
                    {can('users', 'update') && (
                      <button className="btn btn-sm" onClick={() => startEdit(u)}>✏️</button>
                    )}
                    {can('users', 'update') && (
                      <button className="btn btn-sm" title="Reset password"
                              onClick={() => setReset({user: u, newPw: ''})}>🔑</button>
                    )}
                    {Number(u.is_active) === 1 && can('users', 'delete') && (
                      <button className="btn btn-sm" style={{color:'var(--danger)'}}
                              onClick={() => setConfirm(u)}>🗑</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title="Deactivate User?"
        message={confirm ? `Deactivate "${confirm.name}"? They will no longer be able to log in. Their data remains intact.` : ''}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
        confirmLabel="Deactivate"
      />

      {reset && (
        <div className="um-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setReset(null); }}>
          <div className="um-modal">
            <h3>Reset Password for {reset.user.name}</h3>
            <p style={{color:'var(--text-secondary)', fontSize:13}}>
              The user will need to log out and log in again with the new password.
            </p>
            <input type="password" placeholder="New password (min 6 chars)"
                   value={reset.newPw}
                   onChange={e => setReset({...reset, newPw: e.target.value})}
                   style={{width:'100%', padding:'8px 10px', border:'1px solid var(--border)', borderRadius:4, marginTop:10}} />
            <div style={{display:'flex', gap:8, justifyContent:'flex-end', marginTop:16}}>
              <button className="btn btn-sm" onClick={() => setReset(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleResetPassword}>Reset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
